import { FormikErrors } from 'formik';

import { FlightsUpdateDto, FlightTimeType, FlightTurnaroundDto, ResourceType } from '../../../../../../backend';
import { ganttOneMinuteInMilliSeconds } from '../../gantt.constants';

interface FieldType {
    resourceType: ResourceType;
    field: string;
}

//to update if new arrival/departure resource types added
interface Fields {
    arrival: {
        ARR_HALL: FieldType;
    };
    departure: {
        DEP_HALL: FieldType;
        CHECK_IN: FieldType;
        BOARDING_GATE: FieldType;
    };
}

//to update if new arrival/departure resource types added
export const fields: Fields = {
    arrival: {
        ARR_HALL: { resourceType: ResourceType.ARR_HALL, field: 'arrivalHall' },
    },
    departure: {
        DEP_HALL: { resourceType: ResourceType.DEP_HALL, field: 'departureHall' },
        CHECK_IN: { resourceType: ResourceType.CHECK_IN, field: 'counter' },
        BOARDING_GATE: { resourceType: ResourceType.BOARDING_GATE, field: 'departureGate' },
    },
};

export interface TurnaroundFormValues {
    turnaround: FlightTurnaroundDto;
    resourceTypes: ResourceType[];
    estimatedBoardingStart: number | null;
    estimatedBoardingEnd: number | null;
    updateTargetBoardingTimes: boolean;
    actualBoardingStart: number | null;
    actualBoardingEnd: number | null;
    updateActualBoardingStart: boolean;
    updateActualBoardingEnd: boolean;
}

//to update if new arrival/departure resource types added
export const getInitialValues = (turnaround: FlightTurnaroundDto) => {
    return {
        turnaround: {
            considerDepartureFlight: !!turnaround.departureFlight,
            considerArrivalFlight: !!turnaround.arrivalFlight,
            arrivalFlight: turnaround.arrivalFlight
                ? {
                      ...turnaround.arrivalFlight,
                      arrivalParking: turnaround.arrivalFlight.arrivalParking || '',
                      arrivalTerminal: turnaround.arrivalFlight.arrivalTerminal || '',
                      arrivalHall: turnaround.arrivalFlight.arrivalHall || '',
                  }
                : null,
            departureFlight: turnaround.departureFlight
                ? {
                      ...turnaround.departureFlight,
                      departureParking: turnaround.departureFlight.departureParking || '',
                      departureTerminal: turnaround.departureFlight.departureTerminal || '',
                      departureHall: turnaround.departureFlight.departureHall || '',
                      departureGate: turnaround.departureFlight.departureGate || '',
                      counter: turnaround.departureFlight.counter || '',
                  }
                : null,
        },
        resourceTypes: [],
        estimatedBoardingStart: turnaround?.departureFlight?.tsbt || turnaround?.departureFlight?.esbt || 0,
        estimatedBoardingEnd: turnaround?.departureFlight?.tebt || turnaround?.departureFlight?.eebt || 0,
        updateTargetBoardingTimes: false,
        actualBoardingStart: turnaround?.departureFlight?.asbt || 0,
        actualBoardingEnd: turnaround?.departureFlight?.aebt || 0,
        updateActualBoardingStart: false,
        updateActualBoardingEnd: false,
    } as TurnaroundFormValues;
};

export const getFlightsUpdateDto = (values: TurnaroundFormValues) => {
    const arrivalFlightLastRelevantParking =
        values.turnaround.arrivalFlight?.towings &&
        (values.turnaround.arrivalFlight.towings.length > 0
            ? values.turnaround.arrivalFlight.towings[values.turnaround.arrivalFlight.towings.length - 1].parking
            : values.turnaround.arrivalFlight.arrivalParking);

    return {
        turnaround: {
            ...values.turnaround,
            arrivalFlight: values.turnaround.arrivalFlight
                ? {
                      ...values.turnaround.arrivalFlight,
                      // Map values to FlightTimeType.T to indicate that this is a confirmed manual towing
                      towings: values.turnaround.arrivalFlight.towings.map(towing => ({
                          ...towing,
                          time: {
                              ...towing.time,
                              type: FlightTimeType.T,
                          },
                      })),
                  }
                : null,
            departureFlight: values.turnaround.departureFlight
                ? {
                      ...values.turnaround.departureFlight,
                      departureParking: values.turnaround.arrivalFlight
                          ? arrivalFlightLastRelevantParking
                          : values.turnaround.departureFlight.departureParking,
                      tsbt: values.updateTargetBoardingTimes
                          ? values.estimatedBoardingStart
                          : values.turnaround.departureFlight.tsbt,
                      tebt: values.updateTargetBoardingTimes
                          ? values.estimatedBoardingEnd
                          : values.turnaround.departureFlight.tebt,
                      asbt: values.updateActualBoardingStart
                          ? values.actualBoardingStart === 0
                              ? null
                              : values.actualBoardingStart
                          : values.turnaround.departureFlight.asbt,
                      aebt: values.updateActualBoardingEnd
                          ? values.actualBoardingEnd === 0
                              ? null
                              : values.actualBoardingEnd
                          : values.turnaround.departureFlight.aebt,
                  }
                : null,
        },
        updateBoardingStart: values.updateActualBoardingStart,
        updateBoardingEnd: values.updateActualBoardingEnd,
        resourceTypes: values.resourceTypes.reduce(
            (prev, cur) => (prev.indexOf(cur) === -1 ? [...prev, cur] : prev),
            [],
        ),
    } as FlightsUpdateDto;
};

export const validateActualTimes = (errors: FormikErrors<TurnaroundFormValues>, values: TurnaroundFormValues) => {
    const now = new Date().getTime();
    if (values.actualBoardingStart > now) {
        errors['actualBoardingStart'] = 'turnaround.popin.actualBoardingStart.error';
    }

    if (values.actualBoardingEnd > now) {
        errors['actualBoardingEnd'] = 'turnaround.popin.actualBoardingEnd.error';
    }

    if (
        values.actualBoardingEnd !== 0 &&
        values.actualBoardingEnd < values.actualBoardingStart + 5 * ganttOneMinuteInMilliSeconds
    ) {
        errors['actualBoardingEnd'] = 'turnaround.popin.actualBoardingEnd.minimum';
    }

    if (
        (values.actualBoardingStart &&
            !values.actualBoardingEnd &&
            values.estimatedBoardingEnd < values.actualBoardingStart + 20 * ganttOneMinuteInMilliSeconds) ||
        (!values.actualBoardingStart &&
            values.estimatedBoardingEnd &&
            values.estimatedBoardingEnd < values.estimatedBoardingStart + 20 * ganttOneMinuteInMilliSeconds)
    ) {
        errors['estimatedBoardingEnd'] = 'turnaround.popin.estimatedBoardingEnd.minimum';
    }

    if (values.turnaround.arrivalFlight?.towings && values.turnaround.arrivalFlight?.towings.length > 0) {
        // For each towing, if the current towing's time is before the previous towing's time, then the current towing's time is invalid
        // Or if the current towing's time is before the arrival flight's actual parking occupation start time, then the current towing's time is invalid
        // Or if the current towing's time is after the arrival flight's actual parking occupation end time, then the current towing's time is invalid
        values.turnaround.arrivalFlight.towings.forEach((towing, index) => {
            if (index > 0 && towing.time.time < values.turnaround.arrivalFlight?.towings[index - 1].time.time) {
                errors['turnaround.arrivalFlight.towings[' + index + '].time'] =
                    'turnaround.popin.arrivalFlight.towings.time.error';
                errors['turnaround.arrivalFlight.towings'] = 'turnaround.popin.arrivalFlight.towings.time.error';
            } else if (
                towing.time.time <=
                values.turnaround.arrivalFlight?.actualResourceOccupations?.['PARKING']?.occupationStart.time
            ) {
                errors['turnaround.arrivalFlight.towings[' + index + '].time'] =
                    'turnaround.popin.arrivalFlight.towings.time.error';
                errors['turnaround.arrivalFlight.towings'] = 'turnaround.popin.arrivalFlight.towings.time.tooEarly';
            } else if (
                towing.time.time >=
                values.turnaround.arrivalFlight?.actualResourceOccupations?.['PARKING']?.occupationEnd.time
            ) {
                errors['turnaround.arrivalFlight.towings[' + index + '].time'] =
                    'turnaround.popin.arrivalFlight.towings.time.error';
                errors['turnaround.arrivalFlight.towings'] = 'turnaround.popin.arrivalFlight.towings.time.tooLate';
            }
        });
    }
};
